import React from "react";
import PropTypes from "prop-types";
import {useController} from "react-hook-form";
import {Autocomplete, Checkbox, TextField} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SelectTagChip from "../chips/SelectTagChip";
import ItemTooltip from "./ItemTooltip";
import {find, isEmpty, omit} from "underscore";

const CustomInlineSelect = ({
  name,
  label,
  alternateAllLabel,
  required,
  multiple,
  options,
  optionLabel,
  optionChipColor,
  dataTestId,
  defaultValue,
  disabled,
  disableCloseOnSelect,
  size,
  wrapChipLabels = false,
  additionalAll,
}) => {
  const whichLabel = optionLabel || "title";
  const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const optionalDefaultValue = defaultValue ? defaultValue : multiple ? [] : null;
  const {
    field: {onChange, value, ...inputProps},
    fieldState: {error},
  } = useController({
    name,
    rules: {required: required},
    defaultValue: optionalDefaultValue,
  });
  const ALL = {id: 0, value: "ALL", name: "ALL"};
  ALL[whichLabel] = `All - ${alternateAllLabel ?? label}`;
  const addedALL = {id: -1, value: "ALL-Added", name: "ALL-Added"};
  addedALL[whichLabel] = additionalAll?.label;

  const multipleOptions =
    !isEmpty(additionalAll) && !isEmpty(additionalAll?.options) ?
      [ALL, addedALL, ...options] :
      [ALL, ...options];

  return (
    <Autocomplete
      {...inputProps}
      id={name}
      name={name}
      value={value ?? optionalDefaultValue}
      multiple={multiple}
      disableCloseOnSelect={disableCloseOnSelect}
      fullWidth={false}
      disabled={disabled}
      options={multiple && (!value || value?.length < 1) ? multipleOptions : options}
      getOptionLabel={(option) => option[whichLabel] || ""}
      isOptionEqualToValue={(option, value) => {
        if (option && value) {
          return option[whichLabel] === value[whichLabel];
        }
        return false;
      }}
      onChange={(_, data) =>
        onChange(
          data?.[0]?.id === ALL.id ?
            options :
            data?.[0]?.id === addedALL.id ?
              additionalAll.options :
              data,
        )}
      renderOption={
        multiple ?
          (props, option, {selected}) => (
            <li key={props?.key} {...omit(props, "key")}>
              <Checkbox
                icon={uncheckedIcon}
                checkedIcon={checkedIcon}
                style={{marginRight: 8}}
                checked={selected}
                color="primary"
              />
              <ItemTooltip item={option} name={option[whichLabel]} options={options}>
                {option[whichLabel]}
              </ItemTooltip>
            </li>
          ) :
          undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          error={!!error}
          label={label}
          variant="outlined"
          size={size}
          inputProps={{...params.inputProps, "data-testid": dataTestId}}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const {key, ...rest} = getTagProps({index});
          const fallbackDescription =
            !option?.description ?
              find(
                options,
                (o) => o?.value === option[whichLabel] || o?.name === option[whichLabel],
              )
                ?.description :
              undefined;
          return (
            <SelectTagChip
              key={key}
              option={option}
              optionLabel={whichLabel}
              optionChipColor={optionChipColor}
              wrapChipLabels={wrapChipLabels}
              fallbackDescription={fallbackDescription}
              tagProps={rest}
            />
          );
        })}
    />
  );
};

CustomInlineSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  alternateAllLabel: PropTypes.string,
  required: PropTypes.bool,
  multiple: PropTypes.bool,
  options: PropTypes.array.isRequired,
  optionLabel: PropTypes.string,
  optionChipColor: PropTypes.string,
  size: PropTypes.string,
  dataTestId: PropTypes.string,
  disableCloseOnSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultValue: PropTypes.any,
  wrapChipLabels: PropTypes.bool,
  additionalAll: PropTypes.object,
};

export default CustomInlineSelect;
